<template>
  <div ref="autocompleteRef" class="autocomplete">
    <InputWrapper
      ref="containerRef"
      :label="label"
      :mini-label="showMiniLabel"
      :hint="hint ?? ''"
      :disabled="disabled ?? false"
      :error-messages="errorMessages"
      @click="handleClickWrapper"
    >
      <AutoCompleteChips :chips="chips" :small="smallChips" @remove:chip="removeValue">
        <input
          ref="inputRef"
          type="text"
          :name="name"
          :placeholder="placeholderText"
          :value="inputText"
          :disabled="disabled"
          :class="inputBindClass"
          @input="updateSearch"
          @keydown.delete="handleBackspace"
          @focus="handleFocus"
          @focusout="handleFocusOut"
        />
      </AutoCompleteChips>
      <template #append>
        <slot name="append" v-if="!disabled">
          <v-icon
            v-if="showClearButton"
            small
            tabindex="-1"
            data-test="vsautocomplete-clear-option"
            @click.stop="handleClearValue"
          >
            close
          </v-icon>
          <v-icon
            v-else
            tabindex="-1"
            :class="{
              flip: expanded,
            }"
            data-test="vsautocomplete-toggle"
            @click.stop="handleToggle"
          >
            keyboard_arrow_down
          </v-icon>
        </slot>
      </template>
    </InputWrapper>
    <div
      v-if="hasPopover && expanded"
      ref="popoverRef"
      :class="popOverHeight"
      :style="floatingStyles"
      @focusout="handleFocusOut"
    >
      <slot name="popover"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import AutoCompleteChips from '@/components/vision/next/inputs/AutoCompleteChips.vue'
import InputWrapper from '@/components/vision/Inputs/InputWrapper.vue'
import { computed, nextTick, ref, useSlots, inject, type Ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useFloating, autoUpdate, flip, offset, size } from '@floating-ui/vue'
import { useAutoCompleteContext } from './useAutoComplete'

const props = defineProps<{
  name?: string
  label: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  errorMessages?: string[]
  smallChips?: boolean
  keepValueOnClickingOutside?: boolean
  variableHeight?: boolean
}>()

const emit = defineEmits<{
  (event: 'update:search', value: string): void
  (event: 'clear'): void
  (event: 'open'): void
  (event: 'close'): void
  (event: 'close-expand', enableEmitCustomSearchValue: boolean): void
}>()

const slots = useSlots()

// This is used to modify the dropdown width size manually. This is optional.
const customContainer = inject<Ref<HTMLElement | null>>('customContainer', ref(null))

const inputRef = ref<HTMLInputElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const autocompleteRef = ref<HTMLElement | null>(null)

// Use customContainer if available, otherwise fall back to containerRef
const effectiveContainer = computed(() => {
  return customContainer.value || containerRef.value
})

const { chips, inputText, handleBackspace, removeValue, expanded, setExpanded, setModified } =
  useAutoCompleteContext()

const { floatingStyles } = useFloating(effectiveContainer, popoverRef, {
  strategy: 'fixed',
  open: expanded,
  middleware: [
    flip(),
    offset(4),
    size({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        })
      },
    }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true })
  },
})

onClickOutside(autocompleteRef, () => {
  if (!props.keepValueOnClickingOutside) {
    setExpanded(false)
    emit('close-expand', true)
  }
})

const showMiniLabel = computed(
  () => Boolean(inputText.value || chips.value.length) && props.label !== '',
)

const placeholderText = computed(() => {
  // if there is an explict placeholder set, show that
  if (props.placeholder && !inputText.value) {
    return props.placeholder
  }
  // otherwise show the label, but only if there is no value
  if (!inputText.value && !chips.value.length) {
    return props.label
  }
})

const hasPopover = computed(() => {
  return slots.popover
})

const showClearButton = computed(() => {
  return inputText.value || chips.value.length
})

const handleClearValue = () => {
  emit('clear')
  close()
}

const handleToggle = (e: Event) => {
  e.stopImmediatePropagation()
  e.stopPropagation()
  if (expanded.value) {
    close()
  } else {
    inputRef.value?.focus()
  }
}

const handleClickWrapper = () => {
  if (!expanded.value && !props.disabled) {
    inputRef.value?.focus()
  }
}

const updateSearch = (e: Event) => {
  setModified(true)
  emit('update:search', (e.target as HTMLInputElement).value)
}

const handleFocus = () => {
  if (!expanded.value) {
    setExpanded(true)
    setModified(false)

    nextTick().then(() => {
      emit('open')
    })
  }
}

const handleFocusOut = (event: FocusEvent) => {
  // event.relatedTarget is the component that is gaining focus.
  // In Safari, onClick event on button won't focus the button, so event.relatedTarget = null
  // In other browsers, onClick event on button will focus it, so event.relatedTarget = button itself
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
  if (event.relatedTarget && !autocompleteRef.value?.contains(event.relatedTarget as Node)) {
    if (hasPopover?.value) {
      close(true)
    }
  }
}

const focus = () => {
  const checkAndFocus = () => {
    const input = inputRef.value
    if (input && input.offsetParent !== null) {
      input.focus()
    } else {
      requestAnimationFrame(checkAndFocus)
    }
  }
  nextTick(() => checkAndFocus())
}

const close = (enableEmitCustomSearchValue = false) => {
  setExpanded(false)
  setModified(false)
  emit('close')
  emit('close-expand', enableEmitCustomSearchValue)
}

defineExpose({
  input: inputRef,
  setModified,
  close,
  focus,
})

const inputBindClass = computed(() => {
  if (chips.value.length === 0) {
    return { 'input-expanded': true }
  }
  return {
    'input-expanded': expanded.value,
    'input-collapsed': !expanded.value,
  }
})

const popOverHeight = computed(() => {
  if (props.variableHeight) {
    return { 'auto-height': true, popover: true }
  }
  return { 'full-height': true, popover: true }
})
</script>

<style scoped>
.autocomplete {
  position: relative;
  cursor: text;
  width: 100%;
}

.flip {
  transform: rotate(180deg);
}

.input-expanded {
  height: 40px;
  min-height: 40px;
  padding: var(--space-small);
  padding-right: 0;
  transition: all 200ms 0ms;
}

.input-collapsed {
  padding: 0;
  height: 0;
  min-height: 0;
}

.popover {
  z-index: var(--elevation-popover);
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  border-radius: var(--space-base);
  box-sizing: border-box;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  box-shadow: var(--shadow-high);
  width: 100%;
  min-height: 100px;
  max-height: 360px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.auto-height {
  height: auto;
  min-height: 0;
}
</style>
